import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DeviceMobile, Export, PlusSquare, X, Heart } from '@phosphor-icons/react';
import { track } from '../lib/analytics';

const DISMISS_KEY = 'yublink_install_dismissed_until';
const DISMISS_DAYS = 30;

function isIos() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  );
}

function isDismissed() {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return until && Date.now() < until;
  } catch { return false; }
}

function dismiss() {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
  } catch {}
}

export default function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [mode, setMode] = useState(null); // 'android' | 'ios'

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    const iOS = isIos();

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode('android');
      setTimeout(() => setVisible(true), 800);
      track('pwa_install_prompt_available', { platform: 'android' });
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS doesn't fire beforeinstallprompt — show our custom banner after a short delay
    if (iOS) {
      setMode('ios');
      const t = setTimeout(() => setVisible(true), 1200);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const handleClose = () => {
    dismiss();
    setVisible(false);
    setShowIosSheet(false);
    track('pwa_install_dismissed', { platform: mode });
  };

  const handleInstall = async () => {
    if (mode === 'ios') {
      setShowIosSheet(true);
      track('pwa_install_ios_instructions_opened');
      return;
    }
    if (!deferredPrompt) return;
    track('pwa_install_clicked', { platform: 'android' });
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      track('pwa_install_outcome', { platform: 'android', outcome });
      if (outcome === 'accepted') {
        setVisible(false);
      }
    } catch {}
    setDeferredPrompt(null);
  };

  if (!mode) return null;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            data-testid="install-banner"
            className="fixed left-3 right-3 bottom-3 md:left-auto md:right-6 md:bottom-6 md:w-[360px] z-[60]"
          >
            <div className="relative rounded-2xl p-4 bg-[#0B112B] border border-[#D4AF37]/30 shadow-[0_12px_40px_rgba(0,0,0,0.6)] backdrop-blur-md">
              <button
                onClick={handleClose}
                data-testid="install-banner-close"
                className="absolute top-2 right-2 p-1.5 rounded-lg text-[#9EABCE] hover:text-white hover:bg-white/5 transition-all"
                aria-label="Dismiss"
              >
                <X size={14} weight="bold" />
              </button>
              <div className="flex items-start gap-3 pr-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#F0C84A] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(212,175,55,0.3)]">
                  <Heart size={22} weight="fill" className="text-[#060A1A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm font-['Outfit']">Install YuBlink</p>
                  <p className="text-[#9EABCE] text-xs mt-0.5 leading-relaxed">
                    Add YuBlink to your home screen for a native-app experience — faster, fullscreen, offline-ready.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleInstall}
                      data-testid="install-banner-install-btn"
                      className="btn-gold px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5"
                    >
                      <DeviceMobile size={14} weight="fill" /> {mode === 'ios' ? 'How to install' : 'Install'}
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-3 py-2 rounded-lg text-xs font-semibold text-[#9EABCE] hover:text-white hover:bg-white/5 transition-all"
                    >
                      Not now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* iOS Safari instructions sheet */}
      <AnimatePresence>
        {showIosSheet && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#060A1A]/80 backdrop-blur-md flex items-end md:items-center justify-center p-4"
            onClick={handleClose}
            data-testid="install-ios-sheet"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl bg-[#0B112B] border border-[#D4AF37]/25 p-6 shadow-[0_16px_60px_rgba(0,0,0,0.7)]"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#F0C84A] flex items-center justify-center flex-shrink-0">
                  <Heart size={22} weight="fill" className="text-[#060A1A]" />
                </div>
                <div>
                  <p className="text-white font-semibold font-['Outfit']">Install on iPhone</p>
                  <p className="text-[#9EABCE] text-xs">Two quick taps in Safari</p>
                </div>
              </div>
              <ol className="space-y-3 text-sm text-white/90">
                <li className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                  <span className="w-6 h-6 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                  <span className="flex-1">Tap the</span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-semibold">
                    <Export size={14} weight="bold" /> Share
                  </span>
                  <span>button</span>
                </li>
                <li className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                  <span className="w-6 h-6 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                  <span className="flex-1">Select</span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-semibold">
                    <PlusSquare size={14} weight="bold" /> Add to Home Screen
                  </span>
                </li>
              </ol>
              <button
                onClick={handleClose}
                data-testid="install-ios-sheet-close"
                className="mt-5 w-full py-3 rounded-xl btn-gold text-sm font-bold"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
